/** Everything the imprint strip needs to label a shared screenshot. */
export interface ImprintContext {
  /** Brand label drawn bold on the left, e.g. "GammaLens", "DIM". */
  brandLabel: string;
  /** Discord username (no leading @ — added here). */
  username: string;
  /** Right-hand context, e.g. "SPY · 5m", "Landscape", "Big Prints · Sep 3". */
  contextLine: string;
  /** When the capture was taken; rendered as an ET timestamp. */
  takenAt: Date;
}

const FONT_STACK = "Montserrat, Helvetica, Arial, sans-serif";

/** YYYY-MM-DD HH:MM ET, matching the apps' America/New_York convention. */
function formatTimestamp(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")} ET`;
}

/** Pure text builder — split out from the canvas draw so it is unit-testable. */
export function buildImprintText(ctx: ImprintContext): { brand: string; meta: string } {
  return {
    brand: ctx.brandLabel,
    meta: `@${ctx.username} · ${ctx.contextLine} · ${formatTimestamp(ctx.takenAt)}`,
  };
}

/**
 * Draw the slim branded attribution strip along the bottom of the capture.
 * ~3.25% of capture height; fonts scale with the band so it reads on any size.
 * (This is GammaLens's settled "bottomBar" variant, generalised.)
 */
export function applyImprint(canvas: HTMLCanvasElement, ctx: ImprintContext): HTMLCanvasElement {
  const g = canvas.getContext("2d");
  if (!g) return canvas;

  const bandH = Math.max(40, Math.round(canvas.height * 0.0325));
  const y = canvas.height - bandH;
  const brandFontSize = Math.max(22, Math.round(bandH * 0.42));
  const metaFontSize = Math.max(16, Math.round(bandH * 0.3));
  const padX = Math.max(18, Math.round(bandH * 0.3));
  const { brand, meta } = buildImprintText(ctx);

  g.fillStyle = "rgba(13, 18, 32, 0.92)";
  g.fillRect(0, y, canvas.width, bandH);

  g.textBaseline = "middle";

  g.font = `700 ${brandFontSize}px ${FONT_STACK}`;
  g.fillStyle = "#e8eef8";
  g.textAlign = "left";
  g.fillText(brand, padX, y + bandH / 2);

  g.font = `500 ${metaFontSize}px ${FONT_STACK}`;
  g.textAlign = "right";
  g.fillStyle = "rgba(232, 238, 248, 0.88)";
  g.fillText(meta, canvas.width - padX, y + bandH / 2);

  return canvas;
}
